/**
 * Communication Log Modal – React equivalent of Angular's CommunicationLogAddComponent.
 * Opens when a contact name is clicked in the Contact Information table.
 * Posts to /api/communication_log (same API as Angular).
 */

import { useState, useEffect, type FormEvent } from "react"
import { createPortal } from "react-dom"
import { X, Loader2, AlertCircle, Clock } from "lucide-react"
import apiClient from "@/lib/api/apiClient"
import { showSuccess, showError } from "@/lib/utils/notifications"
import { DatePicker } from "@/components/ui/DatePicker"
import modalStyles from "@/features/application/styles/modals.module.css"
import commLogStyles from "@/features/company/styles/communicationLogModal.module.css"
import {
    MentionTextarea,
    applyTaggingToNotes,
} from "@/components/notes/MentionTextarea"
import {
    COMMUNICATION_CATEGORY as COMMUNICATION_CATEGORIES,
    COMMUNICATION_DIRECTION as COMMUNICATION_DIRECTIONS,
    COMMUNICATION_TYPES,
} from '../constants'
import type { CommunicationLogModalProps } from '../types'
import { getRoleDisplayName } from '@/constants/roles'

const inputClass =
    "w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/30 focus:border-[rgb(21_151_255)]"

const inputErrorClass =
    "w-full rounded-lg border border-red-400 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"

const timeInputClass =
    "box-border h-[42px] w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm leading-5 text-slate-900 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/30 focus:border-[rgb(21_151_255)] [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:top-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-9 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"

/* ── Helpers: current date (YYYY-MM-DD) + time (HH:mm) for shared DatePicker + time input ── */
function pad2(n: number) {
    return String(n).padStart(2, "0")
}

function nowLocalDate(): string {
    const d = new Date()
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

function nowLocalTime(): string {
    const d = new Date()
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

/* ── Component ── */
export function CommunicationLogModal({
    isOpen,
    onClose,
    pmcType,
    entityId,
    staffDetail,
    onSuccess,
}: CommunicationLogModalProps) {
    const [type, setType] = useState("")
    const [typeOther, setTypeOther] = useState("")
    const [category, setCategory] = useState("")
    const [categoryOther, setCategoryOther] = useState("")
    const [direction, setDirection] = useState("")
    const [logDate, setLogDate] = useState(nowLocalDate())
    const [logTime, setLogTime] = useState(nowLocalTime())
    const [comment, setComment] = useState("")
    /** @-mention map: `@Name` → `[userid:…]` — same as Liberty Internal Notes / Angular communication log */
    const [taggingContent, setTaggingContent] = useState<Record<string, string>>({})
    const [submitting, setSubmitting] = useState(false)
    const [attempted, setAttempted] = useState(false)

    // Reset form and fire initUser() on open — matches Angular CommunicationLogAddComponent.ngOnInit()
    useEffect(() => {
        if (isOpen) {
            setType("")
            setTypeOther("")
            setCategory("")
            setCategoryOther("")
            setDirection("")
            setLogDate(nowLocalDate())
            setLogTime(nowLocalTime())
            setComment("")
            setTaggingContent({})
            setAttempted(false)
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) return
        const prev = document.body.style.overflow
        document.body.style.overflow = "hidden"
        return () => {
            document.body.style.overflow = prev
        }
    }, [isOpen])

    if (!isOpen || !staffDetail) return null

    const isValid =
        type.trim() !== "" &&
        category.trim() !== "" &&
        direction.trim() !== "" &&
        comment.trim() !== "" &&
        (type !== "OTHER" || typeOther.trim() !== "") &&
        (category !== "OTHER" || categoryOther.trim() !== "")

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        setAttempted(true)
        if (!isValid) return

        setSubmitting(true)
        try {
            const commentToSend = applyTaggingToNotes(comment.trim(), taggingContent ?? {})
            const payload: Record<string, unknown> = {
                type,
                category,
                direction,
                comment: commentToSend,
                pmc_type: pmcType,
                linked_contact: [staffDetail.user_id],
            }
            if (pmcType === "PROPERTY") {
                payload.property_id = entityId
            } else {
                payload.company_id = entityId
            }
            if (type === "OTHER") payload.type_other = typeOther
            else payload.type_other = ""
            if (category === "OTHER") payload.category_other = categoryOther
            else payload.category_other = ""
            if (logDate) {
                // YYYY-MM-DD HH:mm:ss (match Angular moment format)
                const timePart = logTime?.trim() ? logTime.trim() : "00:00"
                payload.log_datetime = `${logDate} ${timePart.length === 5 ? `${timePart}:00` : timePart}`
            }

            await apiClient.post("/api/communication_log", payload)
            showSuccess("Communication Log Added Successfully")
            onSuccess?.()
            onClose()
        } catch (err: unknown) {
            const data = (err as { response?: { data?: unknown } })?.response?.data
            showError(typeof data === 'string' ? data : "Failed to add communication log")
        } finally {
            setSubmitting(false)
        }
    }

    const fieldError = (condition: boolean) =>
        attempted && condition

    const contactSubtitle = staffDetail.role_name
        ? `${staffDetail.name} – ${getRoleDisplayName(staffDetail.role_name)}`
        : staffDetail.name

    const content = (
        <div
            data-testid="company-comm-log-add-overlay"
            className={`${modalStyles.modalOverlay} !z-[10001]`}
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby="comm-log-add-title"
        >
            <div
                data-testid="company-comm-log-add-panel"
                className={`${modalStyles.modalContent} ${modalStyles.modalContent800} ${modalStyles.modalContentStacked}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={commLogStyles.header}>
                    <div className={commLogStyles.headerLead}>
                        <div className="min-w-0">
                            <h3 id="comm-log-add-title" className={commLogStyles.title}>
                                Log Communication
                            </h3>
                        </div>
                    </div>
                    <button
                        data-testid="company-comm-log-add-close-button"
                        type="button"
                        onClick={onClose}
                        className={`${modalStyles.closeButton} ${modalStyles.modalValidationClose}`}
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <form
                    data-testid="company-comm-log-add-form"
                    onSubmit={handleSubmit}
                    className="flex min-h-0 flex-1 flex-col"
                >
                    <div className={commLogStyles.body}>
                        <p className="mb-4 text-sm text-slate-600">{contactSubtitle}</p>
                        <div className={commLogStyles.fieldGrid}>
                            <div className={commLogStyles.field}>
                                <label className={commLogStyles.fieldLabel}>
                                    Type <span className={commLogStyles.required}>*</span>
                                </label>
                                <select
                                    data-testid="company-comm-log-add-type-select"
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                    className={fieldError(!type) ? inputErrorClass : inputClass}
                                >
                                    <option value="">Select Type</option>
                                    {Object.entries(COMMUNICATION_TYPES).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                                {fieldError(!type) && (
                                    <p className={commLogStyles.fieldError}>
                                        <AlertCircle className="h-3 w-3" /> Type selection is required.
                                    </p>
                                )}
                            </div>

                            {type === "OTHER" ? (
                                <div className={commLogStyles.field}>
                                    <label className={commLogStyles.fieldLabel}>
                                        Other Type Reason <span className={commLogStyles.required}>*</span>
                                    </label>
                                    <input
                                        data-testid="company-comm-log-add-type-other-input"
                                        type="text"
                                        value={typeOther}
                                        onChange={(e) => setTypeOther(e.target.value)}
                                        className={fieldError(!typeOther.trim()) ? inputErrorClass : inputClass}
                                    />
                                    {fieldError(!typeOther.trim()) && (
                                        <p className={commLogStyles.fieldError}>
                                            <AlertCircle className="h-3 w-3" /> Other Type is required.
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="hidden sm:block" aria-hidden="true" />
                            )}
                        </div>

                        <div className={`${commLogStyles.fieldGrid} mt-4`}>
                            <div className={commLogStyles.field}>
                                <label className={commLogStyles.fieldLabel}>
                                    Category <span className={commLogStyles.required}>*</span>
                                </label>
                                <select
                                    data-testid="company-comm-log-add-category-select"
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    className={fieldError(!category) ? inputErrorClass : inputClass}
                                >
                                    <option value="">Select Category</option>
                                    {Object.entries(COMMUNICATION_CATEGORIES).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                                {fieldError(!category) && (
                                    <p className={commLogStyles.fieldError}>
                                        <AlertCircle className="h-3 w-3" /> Category selection is required.
                                    </p>
                                )}
                            </div>

                            {category === "OTHER" ? (
                                <div className={commLogStyles.field}>
                                    <label className={commLogStyles.fieldLabel}>
                                        Other Category Reason <span className={commLogStyles.required}>*</span>
                                    </label>
                                    <input
                                        data-testid="company-comm-log-add-category-other-input"
                                        type="text"
                                        value={categoryOther}
                                        onChange={(e) => setCategoryOther(e.target.value)}
                                        className={fieldError(!categoryOther.trim()) ? inputErrorClass : inputClass}
                                    />
                                    {fieldError(!categoryOther.trim()) && (
                                        <p className={commLogStyles.fieldError}>
                                            <AlertCircle className="h-3 w-3" /> Other Category is required.
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="hidden sm:block" aria-hidden="true" />
                            )}
                        </div>

                        <div className={`${commLogStyles.fieldGrid} mt-4`}>
                            <div className={commLogStyles.field}>
                                <label className={commLogStyles.fieldLabel}>Date and Time</label>
                                <div className="flex items-center gap-2">
                                    <div className="min-w-0 flex-1">
                                        <DatePicker
                                            value={logDate}
                                            onChange={setLogDate}
                                            allowFutureDates
                                            showHelperText={false}
                                            placeholder="MM/DD/YYYY"
                                            className="!h-[42px]"
                                            calendarPortalId="company-comm-log-datepicker"
                                            dataTestId="company-comm-log-add-date-input"
                                        />
                                    </div>
                                    <div className="relative w-[118px] shrink-0">
                                        <input
                                            data-testid="company-comm-log-add-time-input"
                                            type="time"
                                            value={logTime}
                                            onChange={(e) => setLogTime(e.target.value)}
                                            className={timeInputClass}
                                        />
                                        <Clock
                                            strokeWidth={1.5}
                                            className="pointer-events-none absolute right-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-gray-400"
                                            aria-hidden
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={commLogStyles.field}>
                                <label className={commLogStyles.fieldLabel}>
                                    Direction <span className={commLogStyles.required}>*</span>
                                </label>
                                <select
                                    data-testid="company-comm-log-add-direction-select"
                                    value={direction}
                                    onChange={(e) => setDirection(e.target.value)}
                                    className={fieldError(!direction) ? inputErrorClass : inputClass}
                                >
                                    <option value="">Select Direction</option>
                                    {Object.entries(COMMUNICATION_DIRECTIONS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                                {fieldError(!direction) && (
                                    <p className={commLogStyles.fieldError}>
                                        <AlertCircle className="h-3 w-3" /> Direction selection is required.
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className={`${commLogStyles.field} mt-4`}>
                            <label className={commLogStyles.fieldLabel}>
                                Comments <span className={commLogStyles.required}>*</span>
                            </label>
                            <MentionTextarea
                                dataTestId="company-comm-log-add-comment-textarea"
                                rows={5}
                                value={comment}
                                onChange={setComment}
                                onTaggingAdd={(displayKey, useridToken) => {
                                    setTaggingContent((prev) => ({
                                        ...prev,
                                        [displayKey]: useridToken,
                                    }))
                                }}
                                disabled={submitting}
                                placeholder="Type your comment here… (use @ to mention Liberty users)"
                                className={`${fieldError(!comment.trim()) ? inputErrorClass : inputClass} resize-y`}
                            />
                            {fieldError(!comment.trim()) && (
                                <p className={commLogStyles.fieldError}>
                                    <AlertCircle className="h-3 w-3" /> Comment is required.
                                </p>
                            )}
                        </div>
                    </div>

                    <div className={commLogStyles.footer}>
                        <div className={`${commLogStyles.footerActions} ${modalStyles.modalFooterActionGroup}`}>
                            <button
                                data-testid="company-comm-log-add-submit-button"
                                type="submit"
                                disabled={!isValid || submitting}
                                className={modalStyles.modalActionPrimary}
                            >
                                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                {submitting ? "Submitting…" : "Submit"}
                            </button>
                            <button
                                data-testid="company-comm-log-add-cancel-button"
                                type="button"
                                onClick={onClose}
                                className={modalStyles.modalActionSecondary}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )

    return createPortal(content, document.body)
}
